import { FC, Fragment } from 'react';

import VoucherRow from './VoucherRow.tsx';

import { VoucherBatch, VoucherSummary } from '@/api/voucher/voucher.types.ts';
import { transformUnixToDateOnly } from '@/utils/dateUtil.ts';
import { capitalizeText } from '@/utils/valueFormatter.ts';

type Props = {
  batch: VoucherBatch;
  expanded: boolean;
  selectedVouchers: string[];
  isUserCorporate: boolean;
  onToggle: (batchId: string) => void;
  onSelectBatch: (batchId: string, isSelected: boolean) => void;
  onSelectVoucher: (voucherId: string, isSelected: boolean) => void;
  onView: (voucher: VoucherSummary) => void;
};

const VoucherBatchRow: FC<Props> = ({
  batch,
  expanded,
  selectedVouchers,
  isUserCorporate,
  onToggle,
  onSelectBatch,
  onSelectVoucher,
  onView,
}) => {
  const isSelected = batch.vouchers.every((v) =>
    selectedVouchers.includes(v.id)
  );
  const isPartiallySelected =
    batch.vouchers.some((v) => selectedVouchers.includes(v.id)) && !isSelected;

  return (
    <Fragment key={batch.batchId}>
      <tr className="hover:bg-gray-50 transition-colors bg-white">
        <td className="px-4 py-4">
          <input
            type="checkbox"
            checked={isSelected}
            ref={(node) => {
              const checkbox = node;
              if (checkbox) {
                checkbox.indeterminate = isPartiallySelected;
              }
            }}
            onChange={() => onSelectBatch(batch.batchId, !isSelected)}
            aria-label={`Select event ${batch.voucherName}`}
            className="h-4 w-4 text-honoursOrange border-honoursOrange rounded focus:ring-honoursOrange cursor-pointer"
            style={{
              accentColor: '#FDA602',
            }}
          />
        </td>
        <td className="px-4 py-4">
          <button
            onClick={() => onToggle(batch.batchId)}
            aria-label={
              expanded
                ? `Collapse event ${batch.voucherName}`
                : `Expand event ${batch.voucherName}`
            }
            className="text-honoursOrange hover:text-orange-600 focus:outline-none"
          >
            <i
              className={`pi ${expanded ? 'pi-chevron-down' : 'pi-chevron-right'}`}
            />
          </button>
        </td>
        <td className="px-4 py-4 text-sm text-gray-900">
          {capitalizeText(batch.voucherName)}
          <span className="ml-2 text-xs text-gray-500">
            ({batch.vouchers.length}{' '}
            {batch.vouchers.length === 1 ? 'voucher' : 'vouchers'})
          </span>
        </td>
        <td className="px-4 py-4 text-sm text-gray-500" aria-hidden="true" />
        <td className="px-4 py-4 text-sm text-gray-500" aria-hidden="true" />
        {!isUserCorporate ? (
          <>
            <td className="px-4 py-4 text-sm text-gray-900">
              {batch.companyName}
            </td>
            <td className="px-4 py-4 text-sm text-gray-900 break-all">
              {batch.companyEmail}
            </td>
          </>
        ) : null}
        <td className="px-4 py-4 text-sm text-gray-900">
          {capitalizeText(batch.type)}
        </td>
        <td className="px-4 py-4 text-sm text-gray-900">
          {transformUnixToDateOnly(batch.validFromUnix)}
        </td>
        <td className="px-4 py-4 text-sm text-gray-500" aria-hidden="true" />
      </tr>

      {expanded
        ? batch.vouchers.map((voucher) => (
            <VoucherRow
              key={voucher.id}
              voucher={voucher}
              selectedVouchers={selectedVouchers}
              isCorporateUser={isUserCorporate}
              onSelectVoucher={onSelectVoucher}
              onView={onView}
            />
          ))
        : null}
    </Fragment>
  );
};

export default VoucherBatchRow;
